import { Form as FormikForm, Formik, FormikErrors, FormikProps, FormikTouched } from 'formik';
import React, { useEffect, useState } from 'react';
import { Col, Row, Spinner, Stack } from 'react-bootstrap';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { BackButton } from '../../../../../components/buttons/BackButton';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import Stepper from '../../../../../components/stepper/Stepper';
import { useToast } from '../../../../../context/ToastContext';
import {
  useCreateInvoiceItemMutation,
  useGetInvoiceItemQuery,
  useUpdateInvoiceItemMutation
} from '../../../../../services/endpoints/finance/invoiceItem';
import { getErrorMessage } from '../../../../../utils/utils';
import { FadeIn } from '../../../../animations/FadeIn';
import { InvoiceItemForm } from './InvoiceItemForm';

const steps = [{ name: 'Details', form: InvoiceItemForm }];

export interface FormValues {
  id?: number;
  invoice_id: string;
  quantity: number | undefined | null;
  unit_price: number | undefined | null;
  enrollment_id?: string;
  description?: string;
  invoice_notes?: string;
}

export interface StepProps {
  errors: Partial<FormikErrors<FormValues>>;
  touched: Partial<FormikTouched<FormValues>>;
  values?: FormValues;
  setFieldValue?: (field: string, value: any, shouldValidate?: boolean) => void;
  readOnly?: boolean;
  invoiceType?: 'flat_rate' | 'per_student';
}

interface AddEditInvoiceItemProps {
  editing?: boolean;
}

export const AddEditInvoiceItem: React.FC<AddEditInvoiceItemProps> = ({ editing }) => {
  const params = useParams();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const [currentStep, setCurrentStep] = useState(0);
  const { addToast } = useToast();
  const navigate = useNavigate();

  const [createInvoiceItem] = useCreateInvoiceItemMutation();
  const [updateInvoiceItem] = useUpdateInvoiceItemMutation();
  const { data, error, isLoading } = useGetInvoiceItemQuery(Number(params.id!), {
    skip: !editing || !params.id
  });

  const [initialValues, setInitialValues] = useState<FormValues>({
    invoice_id: urlParams.get('invoice') ?? '',
    quantity: null,
    unit_price: null
  });

  const validationSchemas = [
    Yup.object({
      invoice_id: Yup.string().required('Invoice selection is required'),
      quantity: Yup.number()
        .positive()
        .nullable()
        .min(1, 'Quantity should be higher than 0')
        .required('Quantity is required'),
      unit_price: Yup.number()
        .positive()
        .nullable()
        .min(0, 'Quantity should be higher or equal to 0')
        .required('Unit price is required'),
      enrollment_id: Yup.string().optional(),
      description: Yup.string().optional(),
      invoice_notes: Yup.string().optional()
    })
  ];

  useEffect(() => {
    if (editing && error && !isLoading) {
      addToast('Error while loading invoice item', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, error, isLoading]);

  useEffect(() => {
    if (data && data.invoiceItem) {
      const { invoiceItem } = data;
      setInitialValues({
        id: invoiceItem.id,
        invoice_id: String(invoiceItem.invoice_id),
        quantity: invoiceItem.quantity,
        unit_price: invoiceItem.unit_price,
        enrollment_id: invoiceItem.enrollment_id ? String(invoiceItem.enrollment_id) : '',
        description: invoiceItem.description ?? undefined,
        invoice_notes: invoiceItem.invoice_notes ?? undefined
      });
    }
  }, [data]);

  const handleAddInvoice = async (formValue: FormValues) => {
    try {
      const payload: any = {
        ...formValue,
        enrollment_id:
            formValue.enrollment_id && formValue.enrollment_id !== ''
                ? Number(formValue.enrollment_id)
                : null,
      };

      if (editing) {
        payload.id = data?.invoiceItem?.id;
        const res = await updateInvoiceItem(payload).unwrap();
        if (res && res.invoice) {
          addToast(`Invoice item updated succesfully`, 'success');
          navigate(`/admin/finance/invoices/${res.invoice.invoice?.id}`);
        }
      } else {
        const res = await createInvoiceItem(payload).unwrap();
        if (res && res.createdInvoiceItem) {
          navigate(`/admin/finance/invoice-items/${res.createdInvoiceItem.id}`);
          addToast('Invoice item created succesfully', 'success');
        } else throw new Error('A problem happened while creating invoice item');
      }
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  const handleNext = async (
    validateForm: () => Promise<FormikErrors<FormValues>>,
    setTouched: (touched: FormikTouched<FormValues>, shouldValidate?: boolean) => void,
    values: FormValues
  ) => {
    const errors = await validateForm();
    if (Object.keys(errors).length === 0) {
      setCurrentStep(currentStep + 1);
    } else {
      setTouched({
        invoice_id: true,
        quantity: true,
        unit_price: true,
        enrollment_id: true,
        description: true,
        invoice_notes: true
      });
    }
  };

  if (isLoading) {
    return (
      <div className="w-100 vh-100 d-inline-flex">
        <Spinner
          variant="primary"
          className="m-auto"
        />
      </div>
    );
  }

  return (
    <FadeIn className="p-lg-4">
      <Stack
        gap={3}
        className="py-lg-0 px-4 py-2"
      >
        <div>
          <BackButton
            text={`Back to invoice${editing ? ' details' : 's'}`}
            color="#2B4E64"
            url={
              editing
                ? `/admin/finance/invoices/${data?.invoiceItem?.invoice_id}`
                : '/admin/finance/invoices'
            }
          />
          <h2
            className="d-none d-lg-block text-info fw-bold mb-0"
            style={{ fontSize: '2.25rem' }}
          >
            {editing ? 'Edit' : 'New'} Invoice Item
          </h2>
          {steps.length > 1 && (
            <Stepper
              steps={steps}
              currentStep={currentStep}
              editing={editing === true}
              onStepClick={(step) => setCurrentStep(step)}
            />
          )}
        </div>

        <div
          className="p-lg-5 p-3"
          style={{
            boxShadow: '0 4px 8px -2px rgba(16, 24, 40, .1),  0 4px 4px 0 rgba(0, 0, 0, .25)',
            border: '1px solid #EBEBEB',
            borderRadius: 10
          }}
        >
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchemas[currentStep]}
            onSubmit={handleAddInvoice}
            enableReinitialize
          >
            {({
              submitForm,
              isSubmitting,
              validateForm,
              touched,
              errors,
              setTouched,
              values,
              setFieldValue
            }: FormikProps<FormValues>) => (
              <FormikForm className="text-start">
                <div style={{ minHeight: '65vh' }}>
                  {currentStep < steps.length &&
                      React.createElement(steps[currentStep]?.form, {
                        errors,
                        touched,
                        values,
                        setFieldValue,
                        invoiceType: (data?.invoiceItem as any)?.enrollment?.course?.invoice_type
                      })}
                </div>

                <Row className="justify-content-end mt-3">
                  <Col
                    xs={6}
                    lg={3}
                  >
                    {currentStep !== 0 && (
                      <SimpleButton
                        disabled={isSubmitting}
                        type="button"
                        onClick={() => setCurrentStep(currentStep - 1)}
                        className="w-100"
                        variant="outline-primary"
                      >
                        Back
                      </SimpleButton>
                    )}
                  </Col>

                  <Col
                    xs={6}
                    lg={3}
                  >
                    <SimpleButton
                      disabled={isSubmitting}
                      type="button"
                      onClick={
                        currentStep === steps.length - 1
                          ? submitForm
                          : () => handleNext(validateForm, setTouched, values)
                      }
                      className="w-100"
                      variant="primary"
                    >
                      {currentStep === steps.length - 1 ? 'Finish' : 'Continue'}
                    </SimpleButton>
                  </Col>
                </Row>
              </FormikForm>
            )}
          </Formik>
        </div>
      </Stack>
    </FadeIn>
  );
};
